import { defineStore } from "pinia"; // Re-triggered for identity update
import api from "./api";

const LANGUAGE_RESOURCES_STORAGE_KEY = "app.languageResourcesCache";

const normalizeCulture = (culture) =>
  typeof culture === "string" && culture.toLowerCase() === "en" ? "en" : "tr";

const normalizeLanguageResourcesPayload = (payload, fallbackCulture = "tr") => {
  const data = payload && typeof payload === "object" ? payload : {};
  const normalizedCulture = normalizeCulture(
    data.culture || data.Culture || fallbackCulture
  );
  const rawResources = data.resources || data.Resources || {};

  const normalizedResources =
    rawResources && typeof rawResources === "object" ? { ...rawResources } : {};

  // Backend may include metadata like $id inside resources object.
  delete normalizedResources.$id;

  return {
    ...data,
    culture: normalizedCulture,
    resources: normalizedResources,
    count:
      typeof data.count === "number"
        ? data.count
        : Object.keys(normalizedResources).length,
  };
};

const loadLanguageResourcesCache = () => {
  if (typeof window === "undefined") return {};

  try {
    const raw = window.localStorage.getItem(LANGUAGE_RESOURCES_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const persistLanguageResourcesCache = (cache) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      LANGUAGE_RESOURCES_STORAGE_KEY,
      JSON.stringify(cache)
    );
  } catch {
    // ignore localStorage persistence failures
  }
};

export const appService = defineStore("AppService", {
  state: () => ({
    currentCulture: "tr",
    languageResourcesCache: loadLanguageResourcesCache(),
    languageResourcesLoading: false,
  }),
  getters: {
    currentLanguageResource(state) {
      return state.languageResourcesCache[state.currentCulture] || null;
    },
    currentLanguageResources() {
      return (
        this.currentLanguageResource?.resources ||
        this.currentLanguageResource?.Resources ||
        {}
      );
    },
    t() {
      return (key, fallback = "") => {
        const value = this.currentLanguageResources[key];
        // console.log(
        //   "Translating key:",
        //   key,
        //   "with fallback:",
        //   fallback,
        //   "->",
        //   value
        // );
        if (typeof value === "string" && value.length) {
          return value;
        }
        //his.createLanguageResource(this.currentCulture, key, fallback);

        if (typeof fallback === "string" && fallback.length) {
          return fallback;
        }

        return key;
      };
    },
  },
  actions: {
    getMenus(culture = "tr") {
      return api().get(
        "/Home/Menus?culture=" + encodeURIComponent(culture || "tr")
      );
    },

    getLanguageResources(culture = "tr") {
      return api().get(
        "/Home/LanguageResources?culture=" + encodeURIComponent(culture || "tr")
      );
    },
    setCurrentCulture(culture = "tr") {
      this.currentCulture = normalizeCulture(culture);
    },
    setLanguageResources(culture, payload) {
      const normalizedCulture = normalizeCulture(culture);

      this.languageResourcesCache = {
        ...this.languageResourcesCache,
        [normalizedCulture]: payload,
      };

      persistLanguageResourcesCache(this.languageResourcesCache);
    },
    async loadLanguageResources(culture = "tr", options = {}) {
      const normalizedCulture = normalizeCulture(culture);
      const { force = false } = options;

      this.setCurrentCulture(normalizedCulture);

      if (!force && this.languageResourcesCache[normalizedCulture]) {
        return this.languageResourcesCache[normalizedCulture];
      }

      this.languageResourcesLoading = true;

      try {
        const response = await this.getLanguageResources(normalizedCulture);
        const payload = normalizeLanguageResourcesPayload(
          response?.data || { culture: normalizedCulture, resources: {} },
          normalizedCulture
        );

        this.setLanguageResources(normalizedCulture, payload);
        return payload;
      } finally {
        this.languageResourcesLoading = false;
      }
    },
    async syncLanguageResources(culture = "tr") {
      const normalizedCulture = normalizeCulture(culture);
      const cached = this.languageResourcesCache[normalizedCulture];

      this.setCurrentCulture(normalizedCulture);

      if (!cached) {
        return this.loadLanguageResources(normalizedCulture, { force: true });
      }

      this.loadLanguageResources(normalizedCulture, { force: true }).catch(
        (error) => console.log(error)
      );
      return cached;
    },
    getLanguageText(key, fallback = "") {
      const text = this.t(key, fallback);

      // If key not found (t returns the key) and fallback provided, create it
      if (text === key && fallback) {
        this.createLanguageResource(this.currentCulture, key, fallback).catch(
          (error) => console.log(error)
        );
      }

      return text;
    },

    createLanguageResource(culture, key, value) {
      return api().post("/Home/LanguageResource", {
        Parameter: key,
        Value: value,
        Culture: culture,
      });
    },
    getMe() {
      return api().get("/User/GetFromAuth");
    },

    getMyOrders() {
      return api().post("/Home/MyOrders");
    },

    getSlugByLanguage(culture, newCulture, slug) {
      return api().post("/Home/GetSlugByLanguage", {
        culture,
        newCulture,
        slug,
      });
    },

    getMyReceipts() {
      return api().post("/Home/ReceiptList");
    },

    getMyReceipt(code) {
      return api().post("/Home/ReceiptDetail", { code: code });
    },

    getMyReceiptResponse(code) {
      return api().post("/Home/ReceiptPayResponse", { code: code });
    },

    getMyEducation(params) {
      return api().post("/Home/getMyEducation", params);
    },

    checkEducationLock(params) {
      return api().post("/Home/EducationLock", params);
    },

    getMyEducations(params) {
      return api().post("/Home/MyEducationList", params);
    },

    getEducationCategoryList(index, size) {
      return api().get(
        "/Home/EducationCategoryList?PageIndex=" + index + "&PageSize=" + size
      );
    },
    getEducationList(index, size) {
      return api().get(
        "/Home/EducationList?PageIndex=" + index + "&PageSize=" + size
      );
    },
    getEducationDetailList(index, size) {
      return api().get(
        "/Home/EducationDetailList?PageIndex=" + index + "&PageSize=" + size
      );
    },
    getEducationDetailUpcomingList(index, size) {
      return api().get(
        "/Home/EducationDetailUpcomingList?PageIndex=" +
          index +
          "&PageSize=" +
          size
      );
    },
    getBlogList(index, size) {
      return api().get(
        "/Home/BlogList?PageIndex=" + index + "&PageSize=" + size
      );
    },
    getNewsList(index, size) {
      return api().get(
        "/Home/NewsList?PageIndex=" + index + "&PageSize=" + size
      );
    },
    getCustomerCommentList(index, size) {
      return api().get(
        "/Home/CustomerCommentList?PageIndex=" + index + "&PageSize=" + size
      );
    },
    getHtmlList(index, size) {
      return api().get(
        "/Home/HtmlList?PageIndex=" + index + "&PageSize=" + size
      );
    },
    getSliderList(index, size) {
      return api().get(
        "/Home/SliderList?PageIndex=" + index + "&PageSize=" + size
      );
    },
    getProductList(index, size, culture) {
      return api().get(
        "/Home/ProductList?PageIndex=" +
          index +
          "&PageSize=" +
          size +
          "&culture=" +
          culture
      );
    },
    getByPage(culture, slug) {
      return api().get("/Home/getByPage?slug=" + slug + "&culture=" + culture);
    },

    //User
    updateUser(params) {
      return api().post("/Home/UpdateUser", params);
    },
    updatePassword(params) {
      return api().post("/Home/UpdatePassword", params);
    },
    updateProfileImage(params) {
      return api().post("/Home/UpdateProfileImage", params);
    },

    //Cart
    addToCart(params) {
      return api().post("/Home/addToCart", params);
    },
    removeToCart(params) {
      return api().post("/Home/removeToCart", params);
    },
    getCartList(index, size) {
      return api().get(
        "/Home/CartList?PageIndex=" + index + "&PageSize=" + size
      );
    },
    getCartDetail() {
      return api().get("/Home/CartDetail");
    },
    getCartItemCount() {
      return api().get("/Home/CartItemCount");
    },
    setInvoiceAddress(params) {
      return api().post("/Home/CartSetInvoiceAddress", params);
    },
    getOrderStatus(params) {
      return api().post("/Home/OrderStatus", params);
    },

    //Destination
    getDestination(id) {
      var postData = {
        id: id,
      };
      return api().post("/Home/Destination", postData);
    },
    setUserDestination(id) {
      var postData = {
        destinationId: id,
      };
      return api().post("/User/SetDestination", postData);
    },
    checkUserDestination() {
      return api().post("/User/CheckDestination");
    },

    //InvoiceAddress
    addInvoiceAddress(params) {
      return api().post("/Home/InvoiceAddress", params);
    },
    deleteInvoiceAddress(id) {
      var postData = {
        id: id,
      };
      return api().post("/Home/DeleteInvoiceAddress", postData);
    },
    getInvoiceAddressList(index, size) {
      var postData = {
        pageRequest: {
          PageIndex: index,
          PageSize: size,
        },
      };
      return api().post("/Home/InvoiceAddressList", postData);
    },

    // addEmployee(params) {
    //   return api().post("/api/user/createEmployee", params);
    // },

    validateHasKey(params) {
      return api().post("/Pay/ValidateHashKey", params);
    },
    pay(params) {
      return api().post("/Pay/PaySmart", params);
    },
    payReceipt(params) {
      return api().post("/Pay/PayReceipt", params);
    },
    transfer(params) {
      return api().post("/Pay/Transfer", params);
    },
    transferReceipt(params) {
      return api().post("/Pay/TransferReceipt", params);
    },
    getPos(params) {
      return api().post("/Pay/CheckBinCode", params);
    },

    // Coach

    getCoachList() {
      return api().get("/Home/CoachList");
    },

    getCoachDashboard() {
      return api().post("/Home/CoachDashboard");
    },
    getPendingCoachSessions() {
      return api().post("/Home/PendingCoachSessions");
    },
    getCompletedCoachSessions() {
      return api().post("/Home/CompletedCoachSessions");
    },
    completeCoachSession(id) {
      return api().post("/Home/CompleteCoachSession", {
        EducationDetailCoachSessionId: id,
      });
    },
    getCoachSessionDetail(id) {
      return api().post("/Home/CoachSessionDetail", {
        Id: id,
      });
    },

    getPendingIndividualCoachSessions() {
      return api().post("/Home/PendingIndividualCoachSessions");
    },
    getCompletedIndividualCoachSessions() {
      return api().post("/Home/CompletedIndividualCoachSessions");
    },
    completeIndividualCoachSession(id) {
      return api().post("/Home/CompleteIndividualCoachSession", {
        Id: id,
      });
    },
    getIndividualCoachSessionDetail(id) {
      return api().post("/Home/IndividualCoachSessionDetail", {
        Id: id,
      });
    },
    getIndividualCoachAssignments() {
      return api().post("/Home/IndividualCoachAssignments");
    },
    getPendingIndividualCoachAssignments() {
      return api().post("/Home/PendingIndividualCoachAssignments");
    },
    getCompletedIndividualCoachAssignments() {
      return api().post("/Home/CompletedIndividualCoachAssignments");
    },
    getCoachAssignments() {
      return api().post("/Home/CoachAssignments");
    },
    getPendingCoachAssignments() {
      return api().post("/Home/PendingCoachAssignments");
    },
    getCompletedCoachAssignments() {
      return api().post("/Home/CompletedCoachAssignments");
    },
    checkIdentityNumber() {
      return api().get("/Home/CheckIdentityNumber");
    },
    updateIdentityNumber(params) {
      return api().post("/Home/UpdateIdentityNumber", params);
    },
    assignCoachByOrder(params) {
      return api().post("/Home/AssignCoachByOrder", params);
    },

    setGraduated() {
      return api().post("/User/SetGraduated");
    },
  },
});
